import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import { PermissionStore } from '@engineering11/access-web'
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button'
import { NotificationHelper } from '@engineering11/web-ui-helpers'
import { TranslateModule } from '@ngx-translate/core'
import { AdminPermissions } from '@admin/app/config/permission.config'
import { ImpersonationService } from '@admin/app/services/impersonation.service'
import { environment } from '@admin/environments/environment'

/**
 * "View as customer" panel injected as a tab into the platform-admin customer view (registered in
 * `dynamic.components.ts`). The SDK sets `selectedCustomerKey` on creation. Gated on
 * `admin/impersonate`; mints a one-time handoff and opens the employer app scoped to the
 * customer in a new tab, leaving this admin session intact in the original tab.
 */
@Component({
  selector: 'admin-act-as-customer',
  standalone: true,
  imports: [CommonModule, TranslateModule, E11ButtonModule],
  template: `
    <div class="e11-p-6" *ngIf="canImpersonate$ | async; else notPermitted">
      <h2 class="e11-text-lg e11-font-primary-bold e11-mb-1">{{ 'View as customer' | translate }}</h2>
      <p class="e11-text-sm e11-text-skin-content-secondary e11-mb-4">
        {{ 'Open the employer app signed in as this customer, in a new tab. Your admin session stays open here.' | translate }}
      </p>
      <e11-button
        [value]="'View as customer' | translate"
        color="primary"
        [loading]="impersonating"
        [disabled]="impersonating || !selectedCustomerKey"
        (click)="actAs()"
      ></e11-button>
    </div>
    <ng-template #notPermitted>
      <div class="e11-p-6 e11-text-sm e11-text-skin-content-secondary">
        {{ 'You do not have permission to act as customers.' | translate }}
      </div>
    </ng-template>
  `,
})
export class ActAsCustomerComponent {
  /** Set by the platform-admin customer view when it instantiates this tab's component. */
  @Input() selectedCustomerKey?: string

  impersonating = false
  canImpersonate$ = this.permissionStore.hasPermission(AdminPermissions.AdminImpersonate)

  constructor(private impersonation: ImpersonationService, private permissionStore: PermissionStore, private notifications: NotificationHelper) {}

  actAs() {
    if (!this.selectedCustomerKey || this.impersonating) return
    this.impersonating = true
    // Open the tab synchronously inside the click so the browser treats it as a user gesture and does
    // not block it — the mint call is async, so opening only after it resolves trips the popup blocker.
    // We point this already-open tab at the handoff URL once the code comes back.
    const handoffTab = window.open('', '_blank')
    this.impersonation.impersonate(this.selectedCustomerKey).subscribe({
      next: res => {
        this.impersonating = false
        // Local testing: point at the locally-served employer instead of the customer's deployed
        // bootstrap URL. The backend still owns the `/#/act-as` path in `employerUrl`; we only swap
        // the host when the override is set (local serve only).
        const override = environment.employerActAsUrlOverride
        const base = override ? `${override}/#/act-as` : res.employerUrl
        const url = this.buildHandoffUrl(base, res.code)
        if (!url) {
          handoffTab?.close()
          this.notifications.popNotification({
            type: 'error',
            title: 'Could not view as this customer',
            message: 'The employer URL configured for this customer is not an allowed host.',
          })
          return
        }
        if (handoffTab) {
          // Sever the reverse link so the employer tab can never script this admin tab.
          handoffTab.opener = null
          handoffTab.location.href = url
        } else {
          window.open(url, '_blank', 'noopener') // popup blocked at click; retry (may prompt to allow)
        }
      },
      error: (err: any) => {
        this.impersonating = false
        handoffTab?.close()
        // Surface the server's reason when present (e.g. a customer with no employer/tenant URL
        // configured returns admin/impersonate-tenant-url), so the admin knows why rather than
        // seeing a generic retry prompt.
        const serverTitle = err?.error?.title ?? err?.error?.message
        this.notifications.popNotification({
          type: 'error',
          title: 'Could not view as this customer',
          message: serverTitle ?? 'Please try again.',
        })
      },
    })
  }

  // Only https (http for the local override) to an allow-listed host; the code is URL-encoded so it can
  // never alter the path or query structure.
  private buildHandoffUrl(base: string, code: string): string | undefined {
    let parsed: URL
    try {
      parsed = new URL(base)
    } catch {
      return undefined
    }
    const allowHttp = !!environment.employerActAsUrlOverride
    if (parsed.protocol !== 'https:' && !(allowHttp && parsed.protocol === 'http:')) return undefined
    if (!new RegExp(environment.actAsAllowedHosts).test(parsed.hostname)) return undefined
    return `${parsed.href}?code=${encodeURIComponent(code)}`
  }
}
